import { selfCheck, validateInput, IsElementRTLInputSchema, UseRTLInputSchema, UseRovingTabIndexInputSchema, findNextEnabledIndex, firstEnabledIndex, lastEnabledIndex, UseArrowKeyNavigationInputSchema, UseFocusTrapInputSchema, UseFocusRestoreInputSchema, UseDisclosureInputSchema, UseTypeaheadInputSchema, UseLiveAnnouncerInputSchema, createLiveAnnouncer, UseLabelledByInputSchema, UseDescribedByInputSchema, DefaultComboboxFilterInputSchema, UseComboboxInputSchema, UseTabsInputSchema, warn } from './chunk-A4N37R74.js';
export { LIB_VERSION, __recordLiveRegionUpdate, __resetLiveRegionTracker, applyLiveRegionAttrs, createLiveAnnouncer, findNextEnabledIndex, firstEnabledIndex, lastEnabledIndex, safeFocus, selfCheck } from './chunk-A4N37R74.js';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { useState, useEffect, useCallback, useRef, useId, useMemo } from 'react';

function isElementRTL(el) {
  validateInput(IsElementRTLInputSchema, "isElementRTL", el);
  if (!el) {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      if (root?.getAttribute("dir") === "rtl") return true;
    }
    return false;
  }
  let cur = el;
  while (cur) {
    const dir = cur.getAttribute?.("dir");
    if (dir === "rtl") return true;
    if (dir === "ltr") return false;
    cur = cur.parentElement;
  }
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    if (root?.getAttribute("dir") === "rtl") return true;
  }
  return false;
}
function useRTL(ref) {
  validateInput(UseRTLInputSchema, "useRTL", void 0);
  const [rtl, setRtl] = useState(() => isElementRTL(ref?.current ?? null));
  useEffect(() => {
    setRtl(isElementRTL(ref?.current ?? null));
  }, [ref]);
  return rtl;
}
function useRovingTabIndex(items, options = {}) {
  validateInput(UseRovingTabIndexInputSchema, "useRovingTabIndex", options);
  const {
    activeIndex = 0,
    orientation = "horizontal",
    loop = true,
    rtl = false,
    manualActivation = false,
    onActivate,
    onMove
  } = options;
  const tabIndex = useCallback(
    (i) => i === activeIndex ? 0 : -1,
    [activeIndex]
  );
  const onKeyDown = useCallback(
    (e) => {
      const list = items.map((ref) => ({ disabled: ref.current?.hasAttribute("disabled") ?? false }));
      const focused = items.findIndex((ref) => ref.current === document.activeElement);
      const from = focused === -1 ? activeIndex : focused;
      const PREV = orientation === "vertical" ? "ArrowUp" : rtl ? "ArrowRight" : "ArrowLeft";
      const NEXT = orientation === "vertical" ? "ArrowDown" : rtl ? "ArrowLeft" : "ArrowRight";
      let target = null;
      if (e.key === NEXT) target = findNextEnabledIndex(list, from, 1, loop);
      else if (e.key === PREV) target = findNextEnabledIndex(list, from, -1, loop);
      else if (e.key === "Home") target = firstEnabledIndex(list);
      else if (e.key === "End") target = lastEnabledIndex(list);
      else if (e.key === "Enter" || e.key === " ") {
        if (focused !== -1 && !list[focused]?.disabled) {
          e.preventDefault();
          onActivate?.(focused);
        }
        return;
      } else {
        return;
      }
      if (target !== null && target !== -1 && target !== from) {
        e.preventDefault();
        items[target]?.current?.focus();
        onMove?.(target);
        if (!manualActivation) onActivate?.(target);
      }
    },
    [items, activeIndex, orientation, loop, rtl, manualActivation, onActivate, onMove]
  );
  return { tabIndex, onKeyDown };
}
function useArrowKeyNavigation(opts) {
  validateInput(UseArrowKeyNavigationInputSchema, "useArrowKeyNavigation", opts);
  const {
    orientation,
    rtl = false,
    itemCount,
    activeIndex,
    onActivate,
    loop = true,
    manualActivation = false,
    disabled
  } = opts;
  const onKeyDown = useCallback(
    (e) => {
      if (itemCount === 0) return;
      const mask = Array.from(
        { length: itemCount },
        (_, i) => ({ disabled: disabled?.[i] ?? false })
      );
      const PREV = orientation === "vertical" ? "ArrowUp" : rtl ? "ArrowRight" : "ArrowLeft";
      const NEXT = orientation === "vertical" ? "ArrowDown" : rtl ? "ArrowLeft" : "ArrowRight";
      let target = null;
      if (e.key === NEXT) target = findNextEnabledIndex(mask, activeIndex, 1, loop);
      else if (e.key === PREV) target = findNextEnabledIndex(mask, activeIndex, -1, loop);
      else if (e.key === "Home") target = firstEnabledIndex(mask);
      else if (e.key === "End") target = lastEnabledIndex(mask);
      else if (!manualActivation && (e.key === "Enter" || e.key === " ")) {
        if (!mask[activeIndex]?.disabled) {
          e.preventDefault();
          onActivate(activeIndex);
        }
        return;
      } else {
        return;
      }
      if (target !== null && target !== -1 && target !== activeIndex) {
        e.preventDefault();
        onActivate(target);
      }
    },
    [orientation, rtl, itemCount, activeIndex, onActivate, loop, manualActivation, disabled]
  );
  return { onKeyDown };
}
var FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
function getFocusables(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
  );
}
function useFocusTrap(containerRef, active) {
  validateInput(UseFocusTrapInputSchema, "useFocusTrap", { active });
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = getFocusables(container);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      if (!activeEl || !container.contains(activeEl)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, containerRef]);
}
function useFocusRestore(active, options = {}) {
  validateInput(UseFocusRestoreInputSchema, "useFocusRestore", { active, ...options });
  const { fallback, preferFallback = false } = options;
  const triggerRef = useRef(null);
  const wasActiveRef = useRef(false);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const preferFallbackRef = useRef(preferFallback);
  preferFallbackRef.current = preferFallback;
  useEffect(() => {
    if (active) {
      triggerRef.current = document.activeElement ?? null;
      wasActiveRef.current = true;
      return;
    }
    if (!wasActiveRef.current) return;
    wasActiveRef.current = false;
    const captured = triggerRef.current;
    triggerRef.current = null;
    const fb = fallbackRef.current?.current ?? null;
    const fbUsable = fb != null && typeof fb.focus === "function";
    if (preferFallbackRef.current && fbUsable) {
      fb.focus();
      return;
    }
    const capturedUsable = captured != null && typeof captured.focus === "function" && captured.isConnected !== false;
    if (capturedUsable) {
      captured.focus();
      return;
    }
    if (fbUsable) {
      fb.focus();
    }
  }, [active]);
}
function useDisclosure(opts = {}) {
  validateInput(UseDisclosureInputSchema, "useDisclosure", opts);
  const { defaultOpen = false, onChange } = opts;
  const [open, setOpen] = useState(defaultOpen);
  const set = useCallback(
    (next) => {
      setOpen((prev) => {
        if (prev === next) return prev;
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );
  const show = useCallback(() => set(true), [set]);
  const hide = useCallback(() => set(false), [set]);
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      onChange?.(next);
      return next;
    });
  }, [onChange]);
  return { open, toggle, show, hide };
}
function useTypeahead(items, getLabel, onMatch, options = {}) {
  validateInput(UseTypeaheadInputSchema, "useTypeahead", options);
  const { resetMs = 500 } = options;
  const bufferRef = useRef("");
  const lastTypedRef = useRef(0);
  const lastMatchRef = useRef(-1);
  const onKeyDown = useCallback(
    (e) => {
      if (e.key.length !== 1) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const char = e.key.toLowerCase();
      const now = Date.now();
      const elapsed = now - lastTypedRef.current;
      lastTypedRef.current = now;
      let isCycle = false;
      if (elapsed >= resetMs) {
        bufferRef.current = char;
      } else if (bufferRef.current === char) {
        isCycle = true;
      } else {
        bufferRef.current = bufferRef.current + char;
      }
      const buffer = bufferRef.current;
      const start = isCycle ? lastMatchRef.current + 1 : 0;
      const n = items.length;
      for (let off = 0; off < n; off++) {
        const i = (start + off) % n;
        const item = items[i];
        if (item === void 0) continue;
        const label = getLabel(item).toLowerCase();
        if (label.startsWith(buffer)) {
          lastMatchRef.current = i;
          onMatch(i);
          return;
        }
      }
    },
    [items, getLabel, onMatch, resetMs]
  );
  return { onKeyDown };
}
function useLiveAnnouncer(opts = {}) {
  validateInput(UseLiveAnnouncerInputSchema, "useLiveAnnouncer", opts);
  const { politeness = "polite", atomic = true, clearAfterMs = 0 } = opts;
  const elRef = useRef(null);
  const announcerRef = useRef(null);
  const optsRef = useRef({ politeness, atomic, clearAfterMs });
  optsRef.current = { politeness, atomic, clearAfterMs };
  const setRef = useCallback((el) => {
    if (announcerRef.current != null) {
      announcerRef.current.destroy();
      announcerRef.current = null;
    }
    elRef.current = el;
  }, []);
  const announce = useCallback((message) => {
    const el = elRef.current;
    if (!el) return;
    let live = announcerRef.current;
    if (live == null || live.element !== el) {
      live = createLiveAnnouncer(el, optsRef.current);
      announcerRef.current = live;
    }
    live.announce(message);
  }, []);
  useEffect(() => {
    return () => {
      if (announcerRef.current != null) {
        announcerRef.current.destroy();
        announcerRef.current = null;
      }
    };
  }, []);
  const regionProps = {
    ref: setRef,
    "aria-live": politeness,
    "aria-atomic": atomic ? "true" : "false",
    role: politeness === "assertive" ? "alert" : "status"
  };
  return { announce, regionProps };
}
function isUsableId(ownId) {
  return typeof ownId === "string" && ownId.trim().length > 0;
}
function useLabelledBy(ownId) {
  validateInput(UseLabelledByInputSchema, "useLabelledBy", ownId);
  const reactId = useId();
  const id = isUsableId(ownId) ? ownId : `lbl-${reactId.replace(/:/g, "")}`;
  return { id, ariaLabelledBy: id };
}
function useDescribedBy(ownId) {
  validateInput(UseDescribedByInputSchema, "useDescribedBy", ownId);
  const reactId = useId();
  const id = isUsableId(ownId) ? ownId : `desc-${reactId.replace(/:/g, "")}`;
  return { id, ariaDescribedBy: id };
}
function defaultComboboxFilter(items, query, toLabel) {
  validateInput(DefaultComboboxFilterInputSchema, "defaultComboboxFilter", { items, query, toLabel });
  const trimmed = query.trim();
  if (trimmed.length === 0) return items.slice();
  const q = fold(trimmed);
  return items.filter((it) => fold(toLabel(it)).includes(q));
}
function fold(s) {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}
function safeId(prefix, raw) {
  return `${prefix}${raw.replace(/[^a-zA-Z0-9-]/g, "")}`;
}
function useCombobox(opts) {
  validateInput(UseComboboxInputSchema, "useCombobox", opts);
  const {
    items,
    value = null,
    onChange,
    values,
    onValuesChange,
    filter,
    isItemDisabled,
    toLabel = (it) => String(it),
    toKey,
    mode: modeOpt,
    multiSelect: multiSelectOpt,
    closeOnSelect: closeOnSelectOpt,
    initialOpen = false,
    loading = false,
    inputId: inputIdOpt,
    listboxId: listboxIdOpt,
    recentItems,
    allowCreate = false,
    onCreate,
    isExactMatch,
    open: openControlled,
    onOpenChange,
    onCommit,
    onSelect
  } = opts;
  const mode = modeOpt ?? (multiSelectOpt ? "multi" : "single");
  const isMulti = mode === "multi";
  const isTypeahead = mode === "typeahead";
  const closeOnSelect = closeOnSelectOpt ?? !isMulti;
  const reactId = useId();
  const inputId = useMemo(
    () => inputIdOpt ?? safeId("cbi", reactId),
    [inputIdOpt, reactId]
  );
  const listboxId = useMemo(
    () => listboxIdOpt ?? safeId("cbl", reactId),
    [listboxIdOpt, reactId]
  );
  const [query, setQuery] = useState("");
  const [openInternal, setOpenInternal] = useState(initialOpen);
  const isOpen = openControlled ?? openInternal;
  const [activeIndex, setActiveIndex] = useState(null);
  const lastValueRef = useRef(value);
  useEffect(() => {
    lastValueRef.current = value;
  }, [value]);
  const filteredItems = useMemo(() => {
    const fn = filter ?? ((arr, q) => defaultComboboxFilter(arr, q, toLabel));
    return fn(items, query);
  }, [items, query, filter, toLabel]);
  const trimmedQuery = query.trim();
  const visibleRecentItems = useMemo(() => {
    if (!recentItems || trimmedQuery.length > 0) return [];
    return recentItems.slice();
  }, [recentItems, trimmedQuery]);
  const visibleItems = visibleRecentItems.length > 0 ? visibleRecentItems : filteredItems;
  const exactMatchExists = useMemo(() => {
    if (!allowCreate || trimmedQuery.length === 0) return false;
    const pred = isExactMatch ?? ((it, q) => fold(toLabel(it)) === fold(q.trim()));
    return items.some((it) => pred(it, trimmedQuery));
  }, [allowCreate, trimmedQuery, items, isExactMatch, toLabel]);
  const showCreateRow = allowCreate && !!onCreate && trimmedQuery.length > 0 && !exactMatchExists;
  const totalRows = visibleItems.length + (showCreateRow ? 1 : 0);
  const createRowIndex = visibleItems.length;
  const itemId = useCallback(
    (index) => `${listboxId}-opt-${index}`,
    [listboxId]
  );
  const createRowId = useCallback(
    () => `${listboxId}-opt-create`,
    [listboxId]
  );
  const setOpen = useCallback(
    (next) => {
      if (openControlled !== void 0) {
        onOpenChange?.(next);
        if (!next) setActiveIndex(null);
        return;
      }
      setOpenInternal((prev) => {
        if (prev === next) return prev;
        if (!next) setActiveIndex(null);
        onOpenChange?.(next);
        return next;
      });
    },
    [openControlled, onOpenChange]
  );
  useEffect(() => {
    if (!isOpen) return;
    if (totalRows === 0) {
      if (activeIndex !== null) setActiveIndex(null);
      return;
    }
    if (activeIndex === null || activeIndex < 0 || activeIndex >= totalRows) {
      const firstEnabled = findFirstEnabled(visibleItems, isItemDisabled);
      if (firstEnabled !== -1) {
        setActiveIndex(firstEnabled);
      } else if (showCreateRow) {
        setActiveIndex(createRowIndex);
      } else {
        setActiveIndex(null);
      }
    }
  }, [totalRows, isOpen]);
  const isSelected = useCallback(
    (item) => {
      if (isMulti) {
        if (!values || values.length === 0) return false;
        if (toKey) {
          const k = toKey(item);
          return values.some((v) => toKey(v) === k);
        }
        return values.includes(item);
      }
      if (value == null) return false;
      if (toKey) return toKey(item) === toKey(value);
      return item === value;
    },
    [isMulti, values, value, toKey]
  );
  const commitCreate = useCallback(() => {
    if (!showCreateRow || !onCreate) return;
    onCreate(trimmedQuery);
    setQuery("");
    if (closeOnSelect) setOpen(false);
  }, [showCreateRow, onCreate, trimmedQuery, closeOnSelect, setOpen]);
  const commit = useCallback(
    (index) => {
      if (showCreateRow && index === createRowIndex) {
        commitCreate();
        return;
      }
      const item = visibleItems[index];
      if (item === void 0) return;
      if (isItemDisabled?.(item)) return;
      if (isMulti) {
        if (onValuesChange && values !== void 0) {
          const k = toKey ? toKey(item) : void 0;
          const exists = k !== void 0 ? values.some((v) => toKey?.(v) === k) : values.includes(item);
          const next = exists ? values.filter(
            (v) => k !== void 0 ? toKey?.(v) !== k : v !== item
          ) : [...values, item];
          onValuesChange(next);
        } else {
          onChange?.(item);
        }
        if (closeOnSelect) {
          setOpen(false);
          setQuery("");
        }
      } else if (isTypeahead) {
        if (onSelect) {
          onSelect(item);
        } else {
          onChange?.(item);
          setQuery(toLabel(item));
        }
        if (closeOnSelect) {
          setOpen(false);
        }
      } else {
        onChange?.(item);
        if (closeOnSelect) {
          setOpen(false);
          setQuery("");
        }
      }
    },
    [
      showCreateRow,
      createRowIndex,
      commitCreate,
      visibleItems,
      isItemDisabled,
      isMulti,
      isTypeahead,
      onValuesChange,
      values,
      toKey,
      onChange,
      onSelect,
      closeOnSelect,
      setOpen,
      toLabel
    ]
  );
  const isIndexDisabled = useCallback(
    (i) => {
      if (i === createRowIndex && showCreateRow) return false;
      const it = visibleItems[i];
      if (it === void 0) return true;
      return !!isItemDisabled?.(it);
    },
    [visibleItems, isItemDisabled, createRowIndex, showCreateRow]
  );
  const moveActive = useCallback(
    (delta) => {
      const n = totalRows;
      if (n === 0) return;
      const start = activeIndex ?? (delta === 1 ? -1 : n);
      for (let off = 1; off <= n; off++) {
        const i = (start + delta * off + n) % n;
        if (isIndexDisabled(i)) continue;
        setActiveIndex(i);
        return;
      }
    },
    [totalRows, activeIndex, isIndexDisabled]
  );
  const onKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) setOpen(true);
          moveActive(1);
          return;
        case "ArrowUp":
          e.preventDefault();
          if (!isOpen) setOpen(true);
          moveActive(-1);
          return;
        case "Home":
          if (!isOpen) return;
          e.preventDefault();
          {
            for (let i = 0; i < totalRows; i++) {
              if (!isIndexDisabled(i)) {
                setActiveIndex(i);
                break;
              }
            }
          }
          return;
        case "End":
          if (!isOpen) return;
          e.preventDefault();
          {
            for (let i = totalRows - 1; i >= 0; i--) {
              if (!isIndexDisabled(i)) {
                setActiveIndex(i);
                break;
              }
            }
          }
          return;
        case "Enter":
          if (isTypeahead && (activeIndex === null || !isOpen)) {
            if (trimmedQuery.length > 0 && onCommit) {
              e.preventDefault();
              onCommit(trimmedQuery);
            }
            return;
          }
          if (!isOpen) return;
          if (activeIndex === null) {
            if (isTypeahead && trimmedQuery.length > 0 && onCommit) {
              e.preventDefault();
              onCommit(trimmedQuery);
            }
            return;
          }
          e.preventDefault();
          commit(activeIndex);
          return;
        case "Escape":
          if (query.length > 0) {
            e.preventDefault();
            setQuery("");
            return;
          }
          if (isOpen) {
            e.preventDefault();
            setOpen(false);
          }
          return;
        default:
          return;
      }
    },
    [
      isOpen,
      query,
      trimmedQuery,
      activeIndex,
      totalRows,
      isIndexDisabled,
      commit,
      moveActive,
      setOpen,
      isTypeahead,
      onCommit
    ]
  );
  const onChangeInput = useCallback(
    (e) => {
      setQuery(e.target.value);
      if (!isOpen) setOpen(true);
    },
    [isOpen, setOpen]
  );
  const onFocus = useCallback(() => {
    if (!isOpen) setOpen(true);
  }, [isOpen, setOpen]);
  const onBlur = useCallback(
    (_e) => {
      setOpen(false);
    },
    [setOpen]
  );
  const isCreateRowActive = showCreateRow && activeIndex !== null && activeIndex === createRowIndex;
  const activeDescendant = isOpen && activeIndex !== null && activeIndex >= 0 && activeIndex < totalRows ? isCreateRowActive ? createRowId() : itemId(activeIndex) : void 0;
  const inputProps = {
    id: inputId,
    role: "combobox",
    "aria-expanded": isOpen,
    "aria-controls": isOpen ? listboxId : void 0,
    "aria-activedescendant": activeDescendant,
    "aria-autocomplete": "list",
    "aria-busy": loading || void 0,
    value: query,
    onChange: onChangeInput,
    onKeyDown,
    onFocus,
    onBlur,
    autoComplete: "off"
  };
  const listboxProps = {
    id: listboxId,
    role: "listbox",
    "aria-busy": loading || void 0,
    hidden: !isOpen
  };
  const getItemProps = useCallback(
    (item, index) => {
      const disabled = isItemDisabled?.(item) ?? false;
      return {
        id: itemId(index),
        role: "option",
        "aria-selected": isSelected(item),
        "aria-disabled": disabled || void 0,
        onClick: () => {
          if (disabled) return;
          commit(index);
        },
        onMouseEnter: () => {
          if (!disabled) setActiveIndex(index);
        },
        onMouseDown: (e) => {
          e.preventDefault();
        }
      };
    },
    [isItemDisabled, itemId, isSelected, commit]
  );
  const getCreateRowProps = useCallback(() => {
    return {
      id: createRowId(),
      role: "option",
      onClick: () => commitCreate(),
      onMouseEnter: () => setActiveIndex(createRowIndex),
      onMouseDown: (e) => {
        e.preventDefault();
      }
    };
  }, [createRowId, commitCreate, createRowIndex]);
  return {
    inputProps,
    listboxProps,
    getItemProps,
    getCreateRowProps,
    isCreateRowActive,
    showCreateRow,
    query,
    setQuery,
    isOpen,
    setOpen,
    activeIndex,
    setActiveIndex,
    filteredItems,
    visibleRecentItems,
    visibleItems
  };
}
function findFirstEnabled(items, isDisabled) {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it === void 0) continue;
    if (!isDisabled?.(it)) return i;
  }
  return -1;
}
function useTabs(opts) {
  validateInput(UseTabsInputSchema, "useTabs", opts);
  const {
    tabs,
    value,
    onChange,
    orientation = "horizontal",
    manualActivation = false,
    idPrefix
  } = opts;
  const reactId = useId();
  const baseId = useMemo(
    () => idPrefix ?? `t${reactId.replace(/[^a-zA-Z0-9-]/g, "")}`,
    [idPrefix, reactId]
  );
  const activeIndex = useMemo(() => {
    const i = tabs.findIndex((t) => t.id === value);
    if (i !== -1 && tabs[i]?.disabled) {
      const env = globalThis.process?.env?.NODE_ENV;
      try {
        warn({
          fn: "useTabs",
          code: "A11Y_INVALID_CONFIG",
          message: `Selected value "${value}" points to a disabled tab \u2014 coercing to the first enabled tab.`,
          prop: "value",
          received: value,
          expected: "an id of a tab whose disabled is not true",
          dedupKey: `useTabs:disabled:${value}`
        });
      } catch {
      }
      if (env === "development") {
        console.error(
          `[a11y/useTabs] Selected value "${value}" points to a disabled tab \u2014 coercing to the first enabled tab.`
        );
      }
      const fb = firstEnabledIndex(tabs);
      return fb === -1 ? 0 : fb;
    }
    if (i === -1) {
      const fb = firstEnabledIndex(tabs);
      return fb === -1 ? 0 : fb;
    }
    return i;
  }, [tabs, value]);
  const [focusedIndex, setFocusedIndex] = useState(activeIndex);
  const tabId = useCallback((id) => `${baseId}-tab-${id}`, [baseId]);
  const panelId = useCallback((id) => `${baseId}-panel-${id}`, [baseId]);
  const activate = useCallback(
    (index) => {
      const t = tabs[index];
      if (!t || t.disabled) return;
      if (t.id !== value) onChange(t.id);
    },
    [tabs, value, onChange]
  );
  const handleKeyDown = useCallback(
    (idx) => (e) => {
      if (tabs.length === 0) return;
      const PREV = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
      const NEXT = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
      let target = null;
      if (e.key === NEXT) target = findNextEnabledIndex(tabs, idx, 1, true);
      else if (e.key === PREV) target = findNextEnabledIndex(tabs, idx, -1, true);
      else if (e.key === "Home") target = firstEnabledIndex(tabs);
      else if (e.key === "End") target = lastEnabledIndex(tabs);
      else if (e.key === "Enter" || e.key === " ") {
        if (manualActivation) {
          e.preventDefault();
          activate(idx);
        }
        return;
      } else {
        return;
      }
      if (target === null || target === -1) return;
      e.preventDefault();
      setFocusedIndex(target);
      if (!manualActivation) activate(target);
    },
    [tabs, orientation, manualActivation, activate]
  );
  const getTabProps = useCallback(
    (id) => {
      const idx = tabs.findIndex((t) => t.id === id);
      const tab = idx === -1 ? void 0 : tabs[idx];
      const isActive = idx === activeIndex;
      return {
        role: "tab",
        id: tabId(id),
        "aria-selected": isActive,
        "aria-controls": panelId(id),
        "aria-disabled": tab?.disabled || void 0,
        tabIndex: isActive ? 0 : -1,
        onKeyDown: handleKeyDown(idx === -1 ? activeIndex : idx),
        onFocus: () => {
          if (idx !== -1) setFocusedIndex(idx);
        },
        onClick: () => activate(idx === -1 ? activeIndex : idx)
      };
    },
    [tabs, activeIndex, tabId, panelId, handleKeyDown, activate]
  );
  const getPanelProps = useCallback(
    (id) => {
      const isActive = tabs[activeIndex]?.id === id;
      return {
        role: "tabpanel",
        id: panelId(id),
        "aria-labelledby": tabId(id),
        hidden: !isActive,
        tabIndex: 0
      };
    },
    [tabs, activeIndex, panelId, tabId]
  );
  const tablistProps = useMemo(
    () => ({ role: "tablist", "aria-orientation": orientation }),
    [orientation]
  );
  return {
    tablistProps,
    getTabProps,
    getPanelProps,
    focusedIndex,
    setFocusedIndex
  };
}

// src/index.ts
try {
  registerLazy(
    "@norkron/web-a11y",
    () => selfCheck
  );
} catch {
}

export { defaultComboboxFilter, isElementRTL, useArrowKeyNavigation, useCombobox, useDescribedBy, useDisclosure, useFocusRestore, useFocusTrap, useLabelledBy, useLiveAnnouncer, useRTL, useRovingTabIndex, useTabs, useTypeahead };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map